/**
 * Messages the customer report sends to the iOS app's SigningRecorder (the
 * report runs inside its WebView) so the recording follows the signing
 * instead of a manual button (13213634281).
 *
 * Signing v2 has two parts (Part 1 = report / Job Summary, Part 2 = the
 * contract) and each part gets its own recording: the report says which part
 * started, when Part 1 is done, and when the contract is sent.
 */
export type SigningRecorderEvent =
  | 'signing-finish'
  | 'signing-part-done'
  | 'signing-start'

export type SigningRecorderPart = 'contract' | 'report'

export interface SigningRecorderMessage {
  type: SigningRecorderEvent
  /** Missing on the single-card (pre-v2) flow. */
  part: SigningRecorderPart | null
}

const TYPES: readonly SigningRecorderEvent[] = [
  'signing-finish',
  'signing-part-done',
  'signing-start',
]

export const signingEventMessage = (
  type: SigningRecorderEvent,
  part: SigningRecorderPart | null = null,
): string => JSON.stringify({ part, type })

/** Parses a WebView message; anything else (other pages, junk) is ignored. */
export const parseSigningEvent = (
  data: string,
): SigningRecorderMessage | null => {
  try {
    const parsed = JSON.parse(data) as {
      part?: unknown
      type?: unknown
    } | null
    const type = parsed?.type
    if (!TYPES.includes(type as SigningRecorderEvent)) return null
    const part =
      parsed?.part === 'report' || parsed?.part === 'contract'
        ? parsed.part
        : null
    return { part, type: type as SigningRecorderEvent }
  } catch {
    return null
  }
}

/**
 * What the recorder should do with a message, given what it is recording now
 * (`current` = the part being recorded, `undefined` = not recording, `null` =
 * recording a part-less / manual session).
 *
 * - start of a part not being recorded: `start` (or `restart` when another
 *   part's recording is still running — it is closed and uploaded first)
 * - Part done of the part being recorded, or finish: `stop`
 * - everything else: `none` (repeats, a part that was never recorded)
 */
export const recorderAction = (
  current: SigningRecorderPart | null | undefined,
  msg: SigningRecorderMessage,
): 'none' | 'restart' | 'start' | 'stop' => {
  const recording = current !== undefined
  if (msg.type === 'signing-start') {
    if (!recording) return 'start'
    if (current === null || msg.part === null || current === msg.part)
      return 'none'
    return 'restart'
  }
  if (!recording) return 'none'
  if (msg.type === 'signing-finish') return 'stop'
  // signing-part-done: only closes that part's own recording; a manual
  // (part-less) recording runs until finish or the rep stops it.
  return current !== null && current === msg.part ? 'stop' : 'none'
}

// ---------------------------------------------------------------------------
// Required recording (13386544331, Jaron Oct 25: "This needs to automatically
// turn on and be required when signing the contract. Not a separate button").
//
// The report can only hold Start until the recording runs if it gets an
// answer back, so the app now replies to every 'signing-start'. The report is
// deployed separately from the app: an older build that never replies must
// not leave signing stuck, so the app first marks the page (before it loads)
// with RECORDER_FLAG. Without the mark the report behaves as before.
// ---------------------------------------------------------------------------

/** Global the iOS app sets on the report page when recording is required. */
export const RECORDER_FLAG = '__ecohomeSigningRecorder'
export const RECORDER_FLAG_VALUE = 'required'
/** window CustomEvent the app dispatches with a SigningRecorderReply. */
export const RECORDER_REPLY_EVENT = 'ecohome:signing-recorder'

export type SigningRecorderReply =
  | { status: 'failed'; reason: string }
  | { status: 'recording' }

/** Script for WebView.injectedJavaScriptBeforeContentLoaded. */
export const recorderFlagScript = (): string =>
  `window[${JSON.stringify(RECORDER_FLAG)}] = ${JSON.stringify(
    RECORDER_FLAG_VALUE,
  )}; true;`

/** Script for WebView.injectJavaScript: hands the reply to the report. */
export const recorderReplyScript = (reply: SigningRecorderReply): string =>
  `window.dispatchEvent(new CustomEvent(${JSON.stringify(
    RECORDER_REPLY_EVENT,
  )}, { detail: ${JSON.stringify(reply)} })); true;`

/** Anything malformed counts as a failure: signing never starts unrecorded. */
export const parseRecorderReply = (detail: unknown): SigningRecorderReply => {
  const d = detail as { reason?: unknown; status?: unknown } | null
  if (d?.status === 'recording') return { status: 'recording' }
  const reason =
    typeof d?.reason === 'string' && d.reason.trim()
      ? d.reason
      : 'The app could not start the recording.'
  return { reason, status: 'failed' }
}
